<template>
  <div
    v-show="true"
    class="cart-content"
  >
    <div
      class="flex justify-between items-center my-4 text-vp-base-500 text-sm"
      :class="props.showHeader ? 'flex' : 'hidden xl:flex'"
    >
      <h3 class="font-bold grow">
        {{ $gettext("Cart") }}
      </h3>
      <button
        v-if="showReset"
        class="underline z-40"
        type="button"
        @click="onReset"
      >
        {{ $gettext("Empty cart") }}
      </button>
    </div>
    <div
      v-if="items.length === 0 || !event"
      class="flex flex-col items-center justify-center grow text-vp-base-500"
    >
      <p class="text-8xl">
        <iconify-icon icon="mdi:cart-variant" />
      </p>
      <p class="text-lg">
        {{ $gettext("Shopping cart is empty") }}
      </p>
    </div>
    <div
      v-else
      class="grow"
    >
      <div :class="props.hideBreakdown ? 'hidden xl:block' : 'block'">
        <div class="bg-vp-base-100 rounded-md">
          <header class="-mr-1 relative">
            <div class="pb-1 flex items-center gap-4">
              <div class="h-16 w-16 overflow-hidden mx-1 rounded-tl-md">
                <img
                  :src="coverImageUrl"
                  :alt="`${event.name} Logo`"
                  class="object-contain h-full w-full"
                >
              </div>
              <div class="pb-3">
                <h2 class="text-lg">
                  {{ event.name }}
                </h2>
                <div class="text-sm text-vp-base-500 inline-flex gap-2">
                  <h3>{{ eventDate }}</h3>
                  <h3>&#x2022;</h3>
                  <h3>{{ event.venue?.name }}</h3>
                </div>
              </div>
            </div>
            <hr
              class="absolute bottom-0 left-0 right-0 border-t-8 border-vp-base-200 border-dotted"
            >
          </header>

          <ShoppingCartItems
            :read-only="readOnly"
            :items="items"
            :control-quantity="totalQuantity"
            :price-breakdown="priceBreakdown"
            :currency="currency"
          />
        </div>

        <div
          v-if="event.config?.discount"
          class="p-2 sm:p-4 bg-vp-base-200 rounded-md my-2 sm:my-4 border border-vp-secondary-3-900"
        >
          <div class="flex flex-row justify-between items-center">
            <h3 class="text-sm sm:text-base text-vp-base-900 uppercase">
              {{ $gettext("Discount code") }}
            </h3>
          </div>
          <form
            class="flex flex-row items-center bg-vp-base-100 rounded-md justify-between"
          >
            <input
              id="discountInput"
              v-model="discountCode"
              class="p-2.5 w-full text-base text-left outline-hidden bg-vp-base-100 rounded-l-md"
              :disabled="disabled"
              :placeholder="$gettext('Discount code')"
              autocomplete="off"
              autocapitalize="off"
              @keydown.enter.prevent="applyDiscount"
            >
            <Accessible>
              <label for="discountInput">{{
                $gettext("Add discount code")
              }}</label>
            </Accessible>
            <span
              class="flex w-min h-full py-2 items-center rounded-r-md px-4 font-bold"
            >
              <VPButton
                class="block min-w-full mx-3 border-vp-secondary-1-400! text-vp-secondary-1-400! rounded-md!"
                :label="$gettext('Apply')"
                variant="outline"
                theme="base-grey"
                size="xs"
                type="button"
                @click.prevent="applyDiscount"
              />
            </span>
          </form>
        </div>

        <div
          v-if="showDonationInput"
          class="p-2 sm:p-4 bg-vp-base-200 rounded-md my-2 sm:my-4"
        >
          <h3 class="text-sm sm:text-base text-vp-base-900">
            {{ $gettext("Donations") }}
          </h3>
          <form class="flex items-center justify-between">
            <input
              id="donationInput"
              class="p-2.5 w-full bg-vp-base-100 text-base text-left outline-hidden rounded-md"
              :disabled="disabled"
              :value="donationAmount"
              :placeholder="$gettext('Your amount')"
              @blur="onDonationInput"
            >
            <Accessible>
              <label for="donationInput">{{ $gettext("Add donation") }}</label>
            </Accessible>
          </form>
          <div class="separator">
            <span class="separator-heading">
              <span class="px-2 text-base">{{ $gettext("OR") }}</span>
            </span>
          </div>
          <div
            class="flex flex-row flex-wrap justify-center gap-1 sm:gap-2"
          >
            <div
              v-for="(amount, index) in donationOptions"
              :key="index"
              class="flex"
            >
              <VPButton
                id="donateButton"
                class="min-w-78 cursor-pointer bg-vp-base-100"
                :label="formatCurrency(amount, { currency, maximumFractionDigits: 0 })"
                variant="outline"
                size="sm"
                @click="setDonationAmount(amount)"
              />
            </div>
          </div>
        </div>

        <div class="my-2 sm:my-4">
          <div class="text-sm text-vp-base-500 pb-2 border-b-2">
            <div
              v-for="item in totalBreakdown"
              :key="item.key"
              class="flex items-center justify-between"
            >
              <span>{{ item.label }}</span>
              <span>{{ formatCurrency(item.amount, { currency }) }}</span>
            </div>
          </div>

          <div class="text-2xl font-bold flex items-center justify-between">
            <span>{{ $gettext("Order Total:") }}</span>
            <span>{{ formatCurrency(priceBreakdown?.orderTotal || 0, { currency }) }}</span>
          </div>
        </div>
      </div>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatCurrency, HARD_CODED_USD_CHANGE_ME } from '@vp/common/util';
import ShoppingCartItems from './ShoppingCartItems.vue';
import VPButton from './components/VPButton.vue';
import { Accessible } from '@vp/vue-components';
import { navigate } from 'vike/client/router';
import { computed, ref, toRef } from 'vue';
import { useEventImage } from './composables/useEventImage';
import { useCheckoutStore } from './stores/checkout';
import { Checkout, Util } from '@vp/common';
import { DateTime } from 'luxon';
import { ShoppingCartItemTypeEnum } from '@vp/common/generated/checkout.graphql';
import { isVPError } from './types/VPError';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';

const { ShoppingCartItemInput } = Checkout;

type Props = {
  showHeader?: boolean;
  hideBreakdown?: boolean;
  readOnly?: boolean;
  showReset?: boolean;
  event: Checkout.CheckoutEvent.CheckoutEvent;
  items: Checkout.ShoppingCartItem.ShoppingCartItem[];
  priceBreakdown: Checkout.PriceBreakdown.PriceBreakdown;
  controlQuantity?: number;
};

const props = defineProps<Props>();

const router = useRouter();

const toast = useToast();

const { $gettext } = useGettext();
const discountCode = ref<string | undefined>();

const event = toRef(props, 'event');

const coverImageUrl = useEventImage(event);

const donationOptions = [10, 25, 50, 100];

const checkoutStore = useCheckoutStore();

const eventDate = computed(() =>
  Util.formatTime(event.value.startTime, {
    timezone: event.value.venue?.timeZone,
    formatOptions: DateTime.DATE_MED_WITH_WEEKDAY,
  }),
);

const currency = computed(() => {
  if (event.value.venue?.currency) {
    return event.value?.venue?.currency;
  }
  return HARD_CODED_USD_CHANGE_ME;
});

const disabled = computed(() => checkoutStore._shoppingCartLoading);

const totalQuantity = computed(
  () => props.controlQuantity || props.items.length || 0,
);

const totalBreakdown = computed(() => (props.priceBreakdown.totalBreakdown || []).filter(({ amount }) => amount != 0));

const showDonationInput = computed(
  () => !props.readOnly && checkoutStore.getEventCheckoutConfig?.donation,
);

const donationAmount = computed(() => {
  const donation = checkoutStore.shoppingCart?.items.find(
    ({ itemType }) => itemType == ShoppingCartItemTypeEnum.Donation,
  );
  if (!donation) {
    return 0;
  }
  return donation.amount;
});

const onDonationInput = (input: FocusEvent) => {
  const amount = parseFloat((<HTMLInputElement>input.target).value);
  if (isNaN(amount)) {
    return;
  }

  setDonationAmount(amount);
};

const setDonationAmount = async (amount: number) => {
  const donation = ShoppingCartItemInput.newDonation(amount);
  await checkoutStore.storeShoppingCartItem(donation);
};

const applyDiscount = async () => {
  const code = discountCode.value?.trim();
  if (!code || code == '') {
    return;
  }

  const discount = ShoppingCartItemInput.newDiscount(code);
  try {
    await checkoutStore.storeShoppingCartItem(discount);
    toast.success($gettext('Discount applied'));
    discountCode.value = '';
  } catch (e) {
    if (isVPError(e)) {
      for (const msg of e.errors) {
        toast.error(msg);
      }
    } else {
      toast.error($gettext('Error applying discount'));
      throw e;
    }
  }
};

const onReset = async () => {
  await checkoutStore.reset();
  await navigate(router.resolve({ name: 'OrderNew' }).href);

  toast.info($gettext('Cart emptied'));
};
</script>
